/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { DataCollectorService } from '../data_collector_services/data_collector_service.service';
import { UtilityService } from '../utility_functions/utility_functions.service';
import { LocalStorageService } from '../localstorage.service';
import moment, { Moment } from 'moment';

@Injectable({
  providedIn: 'root',
})
export class TimezonePickerService {
  private timeZonesData: BehaviorSubject<Array<TimeZoneDataType>> =
    new BehaviorSubject<Array<TimeZoneDataType>>(null);
    
  private _showTimezoneBadge = signal<boolean>(false);
  public showTimezoneBadge = this._showTimezoneBadge.asReadonly();

  constructor(
    private dataCollectorService: DataCollectorService,
    private utilityService: UtilityService,
    private localStorageService: LocalStorageService
  ) {}

  get timeZones(): Observable<Array<TimeZoneDataType>> {
    return this.timeZonesData.asObservable();
  }

  get baseUrl(): any {
    return `${this.dataCollectorService.baseurl()}`;
  }

  get timezoneUrl(): any {
    return `${this.baseUrl}/misc/timezone`;
  }


  getSelectedTimeZone(savedTimeZone: string): string {
    const totalTiZoneData = this.timeZonesData.getValue();
    const searchKey = savedTimeZone;
    const validTimeZone = totalTiZoneData?.find(
      timeZone => timeZone.key === searchKey
    );

    return validTimeZone
      ? `${validTimeZone.display_name}`
      : savedTimeZone;
  }

  getAllTimeZones(): void {
    this.dataCollectorService
      .getData(this.timezoneUrl)
      .subscribe((response) => {
        let timezones: Array<TimeZoneDataType> = response?.data || [];
        timezones = this.updateDisplayNamesUsingDST(timezones,moment.tz(moment(),this.utilityService.preferredTimeZone));
        this.timeZonesData.next(timezones);
      });
  }

  updateDisplayNamesUsingDST(timeZonesData: Array<TimeZoneDataType>, selectedDateTime: Moment): Array<TimeZoneDataType>{
    const formattedDateTime = moment(selectedDateTime).isValid() ?  moment(selectedDateTime) : moment();
    return timeZonesData.map((zone) => {
      // Get offset in format like +05:30
      const gmtOffset = moment.tz(formattedDateTime, zone.key).format('Z');
      const zoneName = zone.display_name.split(') ')[1];
      return {
        ...zone,
        display_name: `(GMT${gmtOffset}) ${zoneName}`,
      };
    });
  }

  customTimezoneSearch(term: string, item: TimeZoneDataType): boolean {
    term = term.trim().toLowerCase();
    return item.display_name.toLowerCase().indexOf(term) > -1 || item.key.toLowerCase().indexOf(term) > -1;
  }

  hasTimezoneDifference(): boolean{
    const userPreferredTimezone = this.localStorageService.getLocalStorage('user_preferences')?.timezone;
    if (!userPreferredTimezone){
      return false
    };
    const currentSystemTimezone = moment.tz.guess();
    const systemOffset = moment.tz(currentSystemTimezone).utcOffset();
    const accountOffset = moment.tz(userPreferredTimezone).utcOffset();
    const hasTimezoneDifference = systemOffset !== accountOffset;
  
    this.localStorageService.setLocalStorage('has_timezone_difference', hasTimezoneDifference);
    if(hasTimezoneDifference){
      const showTimezonePopupState = this.localStorageService.getLocalStorage('show_timezone_popup');
      const showTimezonePopup = showTimezonePopupState !== null ? JSON.parse(showTimezonePopupState) : null;
      if(showTimezonePopup === null){
        this.localStorageService.setLocalStorage('show_timezone_popup', true);
      }
    }
    this._showTimezoneBadge.set(hasTimezoneDifference);
    return hasTimezoneDifference;
  }

  setShowTimezoneBadge(value: boolean): void {
    this._showTimezoneBadge.set(value);
  }

}

export interface TimeZoneDataType {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  display_name: string;
  key: string;
}
